import { useRef, useEffect, forwardRef, useState, useImperativeHandle, useCallback } from "react";
import { ReactComponent as DownArrow } from "../../Assets/icon/down-arrow.svg";
import UserChatComponent from "./UserChatComponent";
import AiChatComponent from "./AiChatComponent";
import "./style.css";

// Pure JavaScript throttle function
const throttle = (func, limit) => {
  let lastFunc;
  let lastRan;
  return function (...args) {
    if (!lastRan) {
      func(...args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func(...args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};

const ChatComponent = forwardRef(
  (
    {
      chatArray,
      onOptionSelected,
      isAiLoading,
      iselements,
      manualEnteredData,
      clearManualData,
      lastQuestID,
      handleEnterWithSubmit,
      isSurvey,
      aiChatRef,
    },
    ref
  ) => {
    const chatContainerRef = useRef(null);
    const spacerRef = useRef(null);
    const messageRefs = useRef({});
    const [showJumpToBottom, setShowJumpToBottom] = useState(false);
    const messageLength = chatArray?.length;
    // Track previous chat meta so we can detect when NEW messages were added
    const prevChatMetaRef = useRef({ length: 0 });
    /** Scroll helpers */
    const getSpacerHeight = () => {
      const spacerEl = spacerRef.current;
      if (!spacerEl) return 0;
      return spacerEl.getBoundingClientRect().height || 0;
    };

    const isAtBottom = (container) => {
      const spacerH = getSpacerHeight();
      if (isSurvey) {
        let boolean = container.scrollHeight - (container.scrollTop + container.clientHeight) < 160;
        return boolean;
      }
      let boolean = container.scrollHeight - spacerH - (container.scrollTop + container.clientHeight) < 48;
      return boolean;
    };

    const scrollToBottom = () => {
      const container = chatContainerRef.current;
      if (!container) return;

      const spacerHeight = getSpacerHeight();
      const offset = isSurvey ? 155 : spacerHeight; // Configurable offset for survey mode
      const targetScroll = container.scrollHeight - container.clientHeight - offset;

      container.scrollTop = Math.max(0, targetScroll);
    };

    const scrollToTop = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = 0;
      }
    };

    // Expose API
    useImperativeHandle(ref, () => ({
      scrollToBottom,
      scrollToTop,
      getContainer: () => chatContainerRef.current,
    }));

    /**
     * Handle initial and new message positioning (top-anchored)
     * We reposition only when NEW messages are added and at least one of them is a user message.
     * This also covers the pattern where the app immediately appends an assistant stub right after the user message.
     */
    useEffect(() => {
      if (!chatArray?.length) return;

      const container = chatContainerRef.current;
      if (!container) return;

      const prev = prevChatMetaRef.current;
      const currLen = chatArray.length;
      const addedCount = Math.max(0, currLen - (prev.length || 0));

      let anchorIndex = null;
      if (addedCount > 0) {
        // Look for the newest user message among the newly added items
        const start = Math.max(0, (prev.length || 0));
        for (let i = currLen - 1; i >= start; i--) {
          if (chatArray[i]?.role === "user") {
            anchorIndex = i;
            break;
          }
        }
      }

      if (anchorIndex != null) {
        const anchorEl = messageRefs.current[anchorIndex];
        if (anchorEl) {
          requestAnimationFrame(() => {
            const elRect = anchorEl.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const offset = containerRect.height * 0.02; // 2% offset from top
            const delta = elRect.top - containerRect.top;
            container.scrollTop += delta - offset;
            // update jump-to-bottom visibility after we reposition
            const atBottom = isAtBottom(container);
            setShowJumpToBottom(!atBottom);
          });
        }
      } else {
        // No new user message; just recompute the jump button (e.g., during streaming)
        const atBottom = isAtBottom(container);
        setShowJumpToBottom(!atBottom);
      }

      // store current meta for next run
      prevChatMetaRef.current = { length: currLen };
    }, [chatArray]);

    /**
     * Do not auto-scroll on AI streaming completion; keep viewport steady.
     * If you want to snap to bottom on manual action, use the FAB.
     */
    useEffect(() => {
      // no-op by design
    }, [isAiLoading]);

    /** Toggle jump-to-bottom button */
    const handleScroll = useCallback(
      throttle(() => {
        if (!chatContainerRef.current) return;
        const container = chatContainerRef.current;
        const atBottom = isAtBottom(container);
        setShowJumpToBottom((prev) => (prev !== !atBottom ? !atBottom : prev));
      }, 400),
      []
    );

    return (
      <div
        className="chat-container-wrapper"
        style={{
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          ref={chatContainerRef}
          className={`chat-container${iselements ? " element-chat-container" : ""}${isSurvey ? " survey-chat-container" : ""}`}
          style={{
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            position: "relative",
            scrollBehavior: "smooth",
          }}
          onScroll={handleScroll}
        >
          {chatArray?.map((chat, index) => (
            <div
              key={index}
              ref={(el) => {
                if (el) {
                  messageRefs.current[index] = el;
                }
              }}
              className={chat?.role === "user" ? "right" : "left"}
            >
              {chat?.role === "user" ? (
                <UserChatComponent chat={chat} />
              ) : (
                <AiChatComponent
                  ref={aiChatRef}
                  chat={chat}
                  isLastPart={index === chatArray.length - 1}
                  isSurvey={chat?.isSurvey}
                  onOptionSelected={onOptionSelected}
                  isAiLoading={isAiLoading}
                  manualEnteredData={manualEnteredData}
                  clearManualData={clearManualData}
                  lastQuestID={lastQuestID}
                  handleEnterWithSubmit={handleEnterWithSubmit}
                  iselements={iselements}
                  chatContainerRef={chatContainerRef}
                />
              )}
            </div>
          ))}
          <div ref={spacerRef} style={{ minHeight: "30dvh" }} />
        </div>

        {/* Jump to bottom (not shown on first new message) */}
        {showJumpToBottom && (
          <div className="chat_jump_to_bottom_icon">
            <button
              className="jump-to-bottom-btn"
              onClick={scrollToBottom}
            >
              <DownArrow />
            </button>
          </div>
        )}
      </div>
    );
  }
);

export default ChatComponent;
